import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import ImportConfig from "@mapmycustomers/shared/types/imports/ImportConfig";
import { Modal, TextField } from "@mapmycustomers/ui";

import { updateConfig } from "@app/scene/settings/component/Imports/store/actions";
import { isUpdatingConfig } from "@app/scene/settings/component/Imports/store/selectors";
import { RootState } from "@app/store/rootReducer";

import styles from "./RenameConfigModal.module.scss";

interface Props {
  config: ImportConfig;
  isUpdating: boolean;
  onHide: () => void;
  showHelperText?: boolean;
  updateConfig: typeof updateConfig.request;
}

const RenameConfigModal: React.FC<Props> = ({
  config,
  isUpdating,
  onHide,
  showHelperText,
  updateConfig,
}) => {
  const intl = useIntl();
  const [error, setError] = useState<string | undefined>();
  const [name, setName] = useState<string>(config.name);

  const handleSave = useCallback(() => {
    if (name.trim().length) {
      setError(undefined);
      updateConfig({ callback: onHide, importConfig: { ...config, name } });
    } else {
      setError(
        intl.formatMessage({
          id: "settings.newImports.config.renameConfigModal.nameField.error.empty",
          defaultMessage: "Name should not be empty",
          description: "Imports edit matching modal input title",
        })
      );
    }
  }, [config, onHide, intl, name, setError, updateConfig]);

  return (
    <Modal
      cancelButtonProps={{ hidden: true }}
      okButtonProps={{ loading: isUpdating }}
      okText={intl.formatMessage({
        id: "settings.newImports.config.renameConfigModal.okButton",
        defaultMessage: "Save changes",
        description: "Imports edit matching modal action",
      })}
      onCancel={onHide}
      onOk={handleSave}
      title={intl.formatMessage({
        id: "settings.newImports.config.renameConfigModal",
        defaultMessage: "Edit this matching",
        description: "Title of imports edit matching modal",
      })}
      visible
    >
      <TextField
        defaultValue={config.name}
        error={error}
        onChange={setName}
        placeholder={intl.formatMessage({
          id: "settings.newImports.config.renameConfigModal.nameField.placeholder",
          defaultMessage: "e.g. Quarterly Lead Import",
          description: "Imports edit matching modal input Placeholder",
        })}
        size="large"
      />
      {showHelperText && (
        <p className={styles.helperText}>
          {intl.formatMessage({
            id: "settings.newImports.config.renameConfigModal.helperText",
            defaultMessage:
              "Looking to modify the matched fields? Open the field matching from the list and make changes directly. Then, opt to update the matching or save as new",
            description: "SubTitle of imports edit matching modal",
          })}
        </p>
      )}
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  isUpdating: isUpdatingConfig(state),
});

const mapDispatchToProps = {
  updateConfig: updateConfig.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(RenameConfigModal);
